import { createContext, useContext, useMemo } from 'react'
import type { ReactNode } from 'react'
import { api } from './api'
import { useAsync } from './useAsync'
import { useAuth } from './auth'
import { listOf, type TreeService, type TreeTopic } from './types'

interface DocsIndex {
  tree: TreeService[]
  loading: boolean
  error: Error | null
  /** Canonical path (`/glossary/en/rx/od`) → doc id. */
  idByPath: Map<string, number>
  /** Doc id → canonical path, for links and for redirecting away from `/d/{id}`. */
  pathById: Map<number, string>
  /** Doc slug → canonical path, so a Pagefind hit URL resolves to an in-app route. */
  pathBySlug: Map<string, string>
}

const Ctx = createContext<DocsIndex | null>(null)

export function DocsIndexProvider({ children }: { children: ReactNode }) {
  // The tree is role-filtered server-side, so refetch when the role settles.
  const { role, loading: authLoading } = useAuth()
  const { data, error, loading } = useAsync(
    (signal) => (authLoading ? Promise.resolve([]) : api.tree(signal)),
    [role, authLoading],
  )

  const value = useMemo<DocsIndex>(() => {
    const tree = data ?? []
    const idByPath = new Map<string, number>()
    const pathById = new Map<number, string>()
    const pathBySlug = new Map<string, string>()

    // `topic.path` is already the full slash path within its service, so it needs
    // no accumulation; `prefix` only covers older responses that omit it.
    const walk = (service: string, prefix: string[], topics: TreeTopic[]) => {
      for (const topic of topics) {
        const segments = topic.path
          ? topic.path.split('/').filter(Boolean)
          : [...prefix, topic.slug]

        for (const doc of listOf(topic.docs)) {
          const path = `/${[service, ...segments, doc.slug].join('/')}`
          idByPath.set(path, doc.id)
          pathById.set(doc.id, path)
          pathBySlug.set(doc.slug, path)
        }
        walk(service, segments, listOf(topic.topics))
      }
    }

    for (const service of tree) {
      for (const doc of listOf(service.docs)) {
        const path = `/${service.slug}/${doc.slug}`
        idByPath.set(path, doc.id)
        pathById.set(doc.id, path)
        pathBySlug.set(doc.slug, path)
      }
      walk(service.slug, [], listOf(service.topics))
    }

    return { tree, loading: loading || authLoading, error, idByPath, pathById, pathBySlug }
  }, [data, loading, authLoading, error])

  return <Ctx.Provider value={value}>{children}</Ctx.Provider>
}

export function useDocsIndex(): DocsIndex {
  const ctx = useContext(Ctx)
  if (!ctx) throw new Error('useDocsIndex must be used inside <DocsIndexProvider>')
  return ctx
}

/**
 * Published pages live at `…/<service>/<topic>/<slug>/index.html`, which is the
 * shape Pagefind reports. Pull the slug so we can route to the doc.
 */
export function slugFromPageUrl(url: string): string | null {
  const parts = url.split('/').filter(Boolean)
  const last = parts.at(-1)
  if (last && /\.html?$/.test(last)) return parts.at(-2) ?? null
  return last ?? null
}
